// src/pages/ProductDetailPage.tsx - VERSION COMPLÈTE avec négociations
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import productService, { Product } from '../services/productService';
import cartService from '../services/cartService';
import negotiationService, { NegotiatedPriceInfo } from '../services/negotiationService';
import PriceNegotiation from '../components/PriceNegotiation';

import { 
  ArrowLeft, 
  ShoppingCart, 
  Plus, 
  Minus,
  Heart,
  Share2,
  Star,
  Truck,
  Shield,
  Package,
  AlertCircle,
  CheckCircle,
  Eye,
  User,
  Tag,
  TrendingDown
} from 'lucide-react';

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);

  // ✅ NOUVEAU - États pour prix négocié
  const [negotiatedPrice, setNegotiatedPrice] = useState<NegotiatedPriceInfo | null>(null);
  const [loadingNegotiatedPrice, setLoadingNegotiatedPrice] = useState(false);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [priceSource, setPriceSource] = useState<'original' | 'negotiated'>('original');

  useEffect(() => {
    if (id) {
      loadProduct(Number(id));
    }
  }, [id]);

  useEffect(() => {
    if (user?.is_customer) {
      loadCart();
    }
  }, [user]);

  // ✅ NOUVEAU - Charger le prix négocié quand le produit est chargé
  useEffect(() => {
    if (product && user?.is_customer && !isOwner()) {
      loadNegotiatedPrice();
    }
  }, [product, user]);

  const loadProduct = async (productId: number) => {
    try {
      setLoading(true);
      setError('');
      const productData = await productService.getProduct(productId);
      
      // Vérifier si le farmer essaie d'accéder à son propre produit
      if (user?.is_farmer && productData.owner_id === user.id) {
        setError('Vous ne pouvez pas acheter vos propres produits. Gérez vos produits depuis votre espace agriculteur.');
        setProduct(null);
        return;
      }
      
      setProduct(productData);
      setCurrentPrice(productData.price);
      setPriceSource('original');
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      setError('Produit non trouvé ou une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const loadCart = async () => {
    if (!user?.is_customer) return;
    
    try {
      const cartData = await cartService.getCart();
      setCartItems(cartData);
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
    }
  };

  // ✅ NOUVEAU - Charger le prix négocié accepté
  const loadNegotiatedPrice = async () => {
    if (!product) return;
    
    try {
      setLoadingNegotiatedPrice(true);
      const negotiatedData = await negotiationService.getNegotiatedPrice(product.id);
      setNegotiatedPrice(negotiatedData);
      
      // Si un prix négocié est disponible, l'utiliser
      if (negotiatedData.has_negotiated_price && negotiatedData.negotiated_price) {
        setCurrentPrice(negotiatedData.negotiated_price);
        setPriceSource('negotiated');
      }
    } catch (error) {
      console.error('Erreur lors du chargement du prix négocié:', error);
    } finally {
      setLoadingNegotiatedPrice(false);
    }
  };

  // ✅ NOUVEAU - Callback quand une négociation est acceptée
  const handleNegotiationAccepted = (finalPrice: number) => {
    setCurrentPrice(finalPrice);
    setPriceSource('negotiated');
    
    // Recharger le prix négocié pour avoir les détails complets
    loadNegotiatedPrice();
  };

  const isOwner = (): boolean => {
    return user?.is_farmer && product?.owner_id === user.id;
  };

  const handleAddToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!user.is_customer) {
      alert('Seuls les clients peuvent ajouter des produits au panier');
      return;
    }

    if (!product) return;

    // Validation
    const validationErrors = cartService.validateAddToCart(quantity, product.stock);
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    try {
      setAddingToCart(true);
      
      // ✅ MODIFIÉ - Utiliser le prix actuel (original ou négocié)
      await cartService.addToCart(product.id, quantity);
      
      await loadCart();
      
      const priceText = priceSource === 'negotiated' 
        ? `au prix négocié de ${cartService.formatPrice(currentPrice)}`
        : `à ${cartService.formatPrice(currentPrice)}`;
      
      alert(`${quantity} x ${product.name} ajouté${quantity > 1 ? 's' : ''} au panier ${priceText} !`);
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    if (!error) {
      navigate('/cart');
    }
  };

  const isProductInCart = (): boolean => {
    return product ? cartService.isProductInCart(cartItems, product.id) : false;
  };

  const getCartItemQuantity = (): number => {
    if (!product) return 0;
    const cartItem = cartService.getCartItemForProduct(cartItems, product.id);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && product && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleShare = () => {
    if (navigator.share && product) {
      navigator.share({
        title: product.name,
        text: product.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier !');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto text-center">
          <div className="h-24 w-24 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-slate mb-2">Produit non trouvé</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 bg-olive text-white px-6 py-3 rounded-md hover:bg-terracotta transition duration-300"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour aux produits
          </Link>
        </div>
      </div>
    );
  }

  // Images du produit
  const productImages = [
    product.image_url,
    product.thumbnail_url
  ].filter(Boolean);

  return (
    <div className="container mx-auto px-4 py-8 bg-beige min-h-screen">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm mb-6">
        <Link to="/products" className="text-olive hover:text-terracotta">
          Produits
        </Link>
        <span className="text-gray-400">•</span>
        <span className="text-slate truncate">{product.name}</span>
      </nav>

      {/* Back Button */}
      <div className="mb-6">
        <Link 
          to="/products"
          className="inline-flex items-center gap-2 text-slate hover:text-terracotta transition duration-300"
        >
          <ArrowLeft className="h-5 w-5" />
          Retour aux produits
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Images Section */}
        <div className="space-y-4">
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
            {productImages[selectedImage] ? (
              <img 
                src={productService.getImageUrl(productImages[selectedImage])} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-24 w-24 text-gray-400" />
              </div>
            )}
          </div>

          {productImages.length > 1 && (
            <div className="flex gap-2">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    selectedImage === index ? 'border-olive' : 'border-gray-200'
                  }`}
                >
                  <img 
                    src={productService.getImageUrl(image)} 
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info Section */}
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-slate mb-2">{product.name}</h1>
            <div className="flex items-center gap-4">
              {/* ✅ NOUVEAU - Affichage du prix avec indication si négocié */}
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-terracotta">
                  {cartService.formatPrice(currentPrice)}
                </span>
                {priceSource === 'negotiated' && (
                  <div className="flex items-center gap-1">
                    <Tag className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">Prix négocié</span>
                  </div>
                )}
              </div>
              
              {/* Afficher le prix original barré si un prix négocié est utilisé */}
              {priceSource === 'negotiated' && negotiatedPrice?.original_price && (
                <div className="text-lg text-gray-500 line-through">
                  {cartService.formatPrice(negotiatedPrice.original_price)}
                </div>
              )}
              
              {product.stock <= 5 && product.stock > 0 && (
                <span className="bg-orange-100 text-orange-800 text-sm px-2 py-1 rounded">
                  Plus que {product.stock} en stock
                </span>
              )}
            </div>

            {/* ✅ NOUVEAU - Afficher l'économie réalisée si prix négocié */}
            {priceSource === 'negotiated' && negotiatedPrice?.savings && negotiatedPrice.savings > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                <div className="flex items-center gap-2 text-green-800">
                  <TrendingDown className="h-5 w-5" />
                  <span className="font-medium">
                    Vous économisez {cartService.formatPrice(negotiatedPrice.savings)} !
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-slate mb-2">Description</h3>
            <p className="text-gray-600 leading-relaxed">{product.description}</p>
          </div>

          {/* Stock Status */}
          <div className="flex items-center gap-2">
            {product.stock > 0 ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-600 font-medium">En stock ({product.stock} disponible{product.stock > 1 ? 's' : ''})</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-600 font-medium">Rupture de stock</span>
              </>
            )}
          </div>

          {/* Vendeur Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-olive rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate">Producteur #{product.owner_id}</p>
                <p className="text-sm text-gray-600">Producteur local certifié</p>
              </div>
            </div>
          </div>

          {/* ✅ NOUVEAU - Composant de négociation */}
          <PriceNegotiation
            productId={product.id}
            productName={product.name}
            originalPrice={product.price}
            stock={product.stock}
            isOwner={isOwner()}
            onNegotiationAccepted={handleNegotiationAccepted}
          />

          {/* Quantity and Actions */}
          {user?.is_customer && !isOwner() && product.stock > 0 && (
            <div className="space-y-4">
              {/* Quantity Selector */}
              <div>
                <label className="block text-sm font-medium text-slate mb-2">
                  Quantité
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  
                  <span className="w-16 text-center font-medium text-lg">
                    {quantity}
                  </span>
                  
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= product.stock}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Cart Status */}
              {isProductInCart() && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span>Déjà dans votre panier ({getCartItemQuantity()} article{getCartItemQuantity() > 1 ? 's' : ''})</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  className="flex-1 flex items-center justify-center gap-2 bg-olive text-white py-3 px-6 rounded-lg hover:bg-terracotta transition duration-300 disabled:opacity-50"
                >
                  {addingToCart ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  ) : (
                    <ShoppingCart className="h-5 w-5" />
                  )}
                  Ajouter au panier
                  {priceSource === 'negotiated' && (
                    <span className="text-xs bg-green-500 px-2 py-1 rounded">Prix négocié</span>
                  )}
                </button>
                
                <button
                  onClick={handleBuyNow}
                  disabled={addingToCart}
                  className="flex-1 bg-terracotta text-white py-3 px-6 rounded-lg hover:bg-olive transition duration-300 disabled:opacity-50"
                >
                  Acheter maintenant
                </button>
              </div>
            </div>
          )}

          {/* Login CTA */}
          {!user && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 mb-3">Connectez-vous pour acheter ce produit ou négocier le prix</p>
              <Link 
                to="/login"
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300"
              >
                Se connecter
              </Link>
            </div>
          )}

          {/* Not Customer CTA */}
          {user && !user.is_customer && !isOwner() && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-600">Vous devez être un client pour acheter des produits</p>
            </div>
          )}

          {/* Owner message */}
          {isOwner() && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">Ceci est votre produit. Gérez-le depuis votre tableau de bord agriculteur.</p>
            </div>
          )}

          {/* Share Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-slate hover:text-terracotta transition duration-300"
            >
              <Share2 className="h-5 w-5" />
              Partager ce produit
            </button>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 text-center">
          <Truck className="h-12 w-12 text-olive mx-auto mb-3" />
          <h3 className="font-semibold text-slate mb-2">Livraison gratuite</h3>
          <p className="text-gray-600 text-sm">Livraison gratuite pour toutes les commandes</p>
        </div>
        
        <div className="bg-white rounded-lg p-6 text-center">
          <Shield className="h-12 w-12 text-olive mx-auto mb-3" />
          <h3 className="font-semibold text-slate mb-2">Produit certifié</h3>
          <p className="text-gray-600 text-sm">Produits frais et de qualité garantie</p>
        </div>
        
        <div className="bg-white rounded-lg p-6 text-center">
          <Package className="h-12 w-12 text-olive mx-auto mb-3" />
          <h3 className="font-semibold text-slate mb-2">Emballage soigné</h3>
          <p className="text-gray-600 text-sm">Emballage respectueux de l'environnement</p>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;